/**
 * ImportSummary - Pre-import summary showing counts by record type
 * Scientific dashboard aesthetic with instrument-style displays
 */

import { cn } from '@/lib/cn';
import { Droplets, UtensilsCrossed, Activity, Anchor, Beaker, CheckCircle2, AlertCircle } from 'lucide-react';

interface ImportPreview {
  total_records: number;
  by_type: Record<string, number>;
  validation_issues: Array<{ record_id: string; issues: string[] }>;
  duplicates_detected: number;
  ready_to_import: number;
}

interface ImportSummaryProps {
  preview: ImportPreview;
  className?: string;
}

const recordTypeConfig: Record<string, { icon: typeof Droplets; label: string; color: string }> = {
  water_reading: {
    icon: Droplets,
    label: 'Water Readings',
    color: 'text-blue-600 bg-blue-50 border-blue-200',
  },
  feeding: {
    icon: UtensilsCrossed,
    label: 'Feedings',
    color: 'text-orange-600 bg-orange-50 border-orange-200',
  },
  sampling: {
    icon: Activity,
    label: 'Samplings',
    color: 'text-purple-600 bg-purple-50 border-purple-200',
  },
  harvest: {
    icon: Anchor,
    label: 'Harvests',
    color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
  },
  application: {
    icon: Beaker,
    label: 'Applications',
    color: 'text-pink-600 bg-pink-50 border-pink-200',
  },
};

export function ImportSummary({ preview, className }: ImportSummaryProps) {
  const hasIssues = preview.validation_issues.length > 0 || preview.duplicates_detected > 0;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Overall Status */}
      <div className={cn(
        'flex items-center gap-3 p-4 rounded-xl border',
        hasIssues
          ? 'bg-amber-50/50 border-amber-200'
          : 'bg-emerald-50/50 border-emerald-200'
      )}>
        {hasIssues ? (
          <AlertCircle className="w-5 h-5 text-amber-600" />
        ) : (
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        )}
        <div className="flex-1">
          <p className={cn(
            'font-semibold',
            hasIssues ? 'text-amber-800' : 'text-emerald-800'
          )}>
            {preview.ready_to_import} of {preview.total_records} records ready to import
          </p>
          {hasIssues && (
            <p className="text-sm text-amber-600 mt-0.5">
              {preview.duplicates_detected > 0 && `${preview.duplicates_detected} duplicates detected. `}
              {preview.validation_issues.length > 0 && `${preview.validation_issues.length} with validation issues.`}
            </p>
          )}
        </div>
      </div>

      {/* Records by Type - Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {Object.entries(preview.by_type).map(([type, count]) => {
          const config = recordTypeConfig[type] || {
            icon: Activity,
            label: type,
            color: 'text-gray-600 bg-gray-50 border-gray-200',
          };
          const Icon = config.icon;

          return (
            <div
              key={type}
              className={cn(
                'flex flex-col items-center p-3 rounded-lg border',
                config.color
              )}
            >
              <Icon className="w-5 h-5 mb-1" />
              <span className="text-2xl font-bold font-mono">{count}</span>
              <span className="text-xs opacity-70 text-center">{config.label}</span>
            </div>
          );
        })}
      </div>

      {/* Validation Issues Detail */}
      {preview.validation_issues.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Records with Issues</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {preview.validation_issues.slice(0, 10).map((issue, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className="text-gray-400 font-mono">#{issue.record_id.slice(0, 8)}</span>
                <span className="text-red-600">{issue.issues.join(', ')}</span>
              </div>
            ))}
            {preview.validation_issues.length > 10 && (
              <p className="text-xs text-gray-500 italic">
                + {preview.validation_issues.length - 10} more...
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ImportResultSummary - Shows result after import completes
 */
interface ImportResult {
  total_imported: number;
  total_failed: number;
  imported: Array<{ staging_id: string; main_id: string; record_type: string }>;
  failed: Array<{ staging_id: string; errors: string[] }>;
}

interface ImportResultSummaryProps {
  result: ImportResult;
  className?: string;
}

export function ImportResultSummary({ result, className }: ImportResultSummaryProps) {
  const allSuccess = result.total_failed === 0;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Status Banner */}
      <div className={cn(
        'flex items-center gap-3 p-4 rounded-xl border',
        allSuccess
          ? 'bg-emerald-50 border-emerald-200'
          : 'bg-amber-50 border-amber-200'
      )}>
        {allSuccess ? (
          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
        ) : (
          <AlertCircle className="w-6 h-6 text-amber-600" />
        )}
        <div>
          <p className={cn(
            'font-semibold',
            allSuccess ? 'text-emerald-800' : 'text-amber-800'
          )}>
            {allSuccess
              ? `Successfully imported ${result.total_imported} records`
              : `Imported ${result.total_imported} records, ${result.total_failed} failed`}
          </p>
        </div>
      </div>

      {/* Failed records detail */}
      {result.failed.length > 0 && (
        <div className="bg-red-50/50 rounded-lg p-3 border border-red-200">
          <h4 className="text-sm font-medium text-red-800 mb-2">Failed Records</h4>
          <div className="space-y-2">
            {result.failed.map((fail, i) => (
              <div key={i} className="text-sm">
                <span className="font-mono text-xs text-red-600">
                  {fail.staging_id.slice(0, 8)}:
                </span>
                <span className="text-red-700 ml-2">{fail.errors.join('; ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
